import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Crown, Zap, Check, Loader2, ArrowRight, Sparkles } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";

interface UpgradeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  feature?: string;
  currentPlan?: string;
  message?: string;
}

const UPGRADE_OPTIONS = [
  {
    plan: "starter",
    name: "Starter",
    price: "£9.99/month",
    icon: Zap,
    color: "from-blue-500 to-cyan-600",
    highlights: [
      "2 child profiles",
      "10 sessions per day",
      "Mock tests & comprehension",
      "Full analytics + insights",
    ],
  },
  {
    plan: "pro",
    name: "Pro",
    price: "£19.99/month",
    icon: Crown,
    color: "from-purple-500 to-violet-600",
    highlights: [
      "5 child profiles",
      "Unlimited sessions & lessons",
      "Unlimited mock tests",
      "AI revision timetable",
    ],
  },
];

export function UpgradeModal({ open, onOpenChange, feature, currentPlan = "free", message }: UpgradeModalProps) {
  const [loading, setLoading] = useState<string | null>(null);
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const displayMessage = message || (feature ? `This feature requires a plan upgrade.` : "Upgrade your plan to unlock more features.");

  const availableOptions = UPGRADE_OPTIONS.filter(opt => {
    const planOrder = { free: 0, starter: 1, pro: 2 };
    return (planOrder[opt.plan as keyof typeof planOrder] ?? 0) > (planOrder[currentPlan as keyof typeof planOrder] ?? 0);
  });

  const handleUpgrade = async (plan: string) => {
    setLoading(plan);
    try {
      const res = await apiRequest("POST", "/api/subscriptions/create-checkout", { plan });
      const data = await res.json();

      if (data.demo) {
        toast({ title: "Plan Updated", description: data.message });
        queryClient.invalidateQueries({ queryKey: ["/api/subscriptions/status"] });
        queryClient.invalidateQueries({ queryKey: ["/api/entitlement"] });
        onOpenChange(false);
      } else if (data.url) {
        window.location.href = data.url;
      }
    } catch (e: any) {
      toast({ title: "Error", description: "Could not start checkout. Please try again.", variant: "destructive" });
    }
    setLoading(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-white/10 text-white">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <DialogTitle className="text-xl font-display text-white" data-testid="text-upgrade-title">
              Unlock More with Zenova
            </DialogTitle>
          </div>
          <DialogDescription className="text-slate-400" data-testid="text-upgrade-message">
            {displayMessage}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {availableOptions.map((option) => {
            const Icon = option.icon;
            return (
              <div
                key={option.plan}
                className="bg-white/[0.06] border border-white/10 rounded-2xl p-5 hover:border-white/20 transition-all"
                data-testid={`upgrade-option-${option.plan}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${option.color} flex items-center justify-center`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-lg">{option.name}</h3>
                      <p className="text-slate-400 text-sm">{option.price}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 mb-4">
                  {option.highlights.map((h, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
                      <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                      <span>{h}</span>
                    </div>
                  ))}
                </div>

                <Button
                  onClick={() => handleUpgrade(option.plan)}
                  disabled={loading === option.plan}
                  className={`w-full h-11 rounded-xl font-semibold bg-gradient-to-r ${option.color} hover:opacity-90 text-white`}
                  data-testid={`button-upgrade-${option.plan}`}
                >
                  {loading === option.plan ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Upgrade to {option.name} <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        <div className="mt-2 text-center">
          <button
            onClick={() => { onOpenChange(false); setLocation("/pricing"); }}
            className="text-sm text-slate-500 hover:text-slate-300 transition-colors"
            data-testid="link-compare-plans"
          >
            Compare all plans in detail
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

let upgradeModalState: {
  setOpen: (open: boolean) => void;
  setFeature: (feature: string) => void;
  setMessage: (message: string) => void;
  setPlan: (plan: string) => void;
} | null = null;

export function registerUpgradeModal(handlers: typeof upgradeModalState) {
  upgradeModalState = handlers;
}

export function showUpgradeModal(opts: { feature?: string; message?: string; plan?: string } = {}) {
  if (upgradeModalState) {
    if (opts.feature) upgradeModalState.setFeature(opts.feature);
    if (opts.message) upgradeModalState.setMessage(opts.message);
    if (opts.plan) upgradeModalState.setPlan(opts.plan);
    upgradeModalState.setOpen(true);
  }
}
